import { useEffect, useState } from 'react';

import {
  ArrowButton, Button, ColumnSortEnum, Modal, Table, TableMetadata,
  TableStateWithFilters,
  TextField, Typography, useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ExportTypeEnum, PagedResult } from '@king-ict/eupisi-ui/models';
import { nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { deleteKnowledgeCheck, SearchKnowledgeCheckRequest, searchKnowledgeChecks, exportKnowledgeChecks } from '../../api/nisooApi';
import { useDidUpdateEffect, useInput } from "../../hooks";
import { CodeBookSearch } from '../../models/CodeBook/CodeBookSearch';
import { KnowledgeCheck, KnowledgeCheckSort } from '../../models/KnowledgeCheck';
import { SortDirection } from '../../models/Shared';
import { RoleEnum } from '../../models/User/RoleEnum';
import { useGetUserSessionQuery } from '../../queries/userQuery';
import { codebookRouteKnowledgeCheck } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookKnowledgeCheck(): JSX.Element {
  const storageKey = 'CodeBookKnowledgeCheckSearchTableState';

  const createInitialTableState = (): TableStateWithFilters<CodeBookSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        name: "",
        page: 1,
        pageSize: 15 as any,
        sortBy: KnowledgeCheckSort.Name,
        order: ColumnSortEnum.ASC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<CodeBookSearch>;
    return result;
  }

  const createInitialSearch = (initialTableState: TableStateWithFilters<CodeBookSearch>): CodeBookSearch => {
    return {
      name: initialTableState.name
    }
  }

  const createInitialKnowledgeChecks = (): PagedResult<KnowledgeCheck> => {
    return {
      currentPage: 1,
      firstRowOnPage: 0,
      lastRowOnPage: 0,
      pageCount: 0,
      pageSize: 10,
      rowCount: 0,
      results: [],
    }
  }

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [initialTableState] = useState<TableStateWithFilters<CodeBookSearch>>(() => createInitialTableState());
  const [searchFilter, setSearchFilter] = useState<CodeBookSearch>(() => createInitialSearch(initialTableState));
  const [knowledgeChecks, setKnowledgeChecks] = useState<PagedResult<KnowledgeCheck>>(() => createInitialKnowledgeChecks());
  const [knowledgeCheckToDelete, setKnowledgeCheckToDelete] = useState<KnowledgeCheck>()

  const { data: userSession } = useGetUserSessionQuery();
  const userCanEditData = userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin 
    || userSession.roleId === RoleEnum.ASOOAdmin || userSession.roleId === RoleEnum.Helpdesk);

  const tableMetadata: TableMetadata<KnowledgeCheck> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "name",
        header: t("common.name"),
        accessor: "name",
        sortBy: KnowledgeCheckSort.Name
      },
      {
        columnKey: "canDelete",
        header: "",
        accessor: (item) => {
          return (
            <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={() => deleteItem(item)}>
              {item.canDelete && userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin 
                || userSession.roleId === RoleEnum.ASOOAdmin || userSession.roleId === RoleEnum.Helpdesk) ? t("common.remove") : ""}
            </Typography>
          )
        }
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("codeBook.knowledgeChecks.goto")}
              onClick={() => { navigateToDetails(item) }}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  var [tableProps, tableState, setFilters] = useTable<CodeBookSearch>(
    {
      metadata: tableMetadata,
      data: knowledgeChecks.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: knowledgeChecks.rowCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  useEffect(() => {
    if (!userSession || (userSession.roleId === RoleEnum.InstitutionAdmin || userSession.roleId === RoleEnum.InstitutionDirector)) {
      return;
    }

    callApiSearch();
    // search change shouldn't trigger this fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, userSession]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...searchFilter,
      sortBy: tableState.sortBy ?? KnowledgeCheckSort.Name,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    }));
  }, [tableState])

  const callApiSearch = async () => {
    const request: SearchKnowledgeCheckRequest = {
      sortBy: tableState.sortBy ?? KnowledgeCheckSort.Name,
      sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 15,
      search: searchFilter.name
    }

    var result = await searchKnowledgeChecks(request);
    setKnowledgeChecks(result);
  }

  const navigateToDetails = (item: KnowledgeCheck) => {
    history.push(
      codebookRouteKnowledgeCheck(),
      {
        page: "details",
        knowledgeCheckId: item.id
      }
    );
  }

  const search = async () => {
    setFilters(searchFilter);
  };

  const clearFilters = async () => {
    const clearSearchFilter = { name: "" };
    setSearchFilter(clearSearchFilter);
    setFilters(clearSearchFilter);
  };

  const deleteItem = async (item: KnowledgeCheck) => {
    if (!item.canDelete) {
      return;
    }

    setKnowledgeCheckToDelete(item);
    setOpenConfirmModal(true);
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    await deleteKnowledgeCheck(knowledgeCheckToDelete!.id!);
    await callApiSearch();
  }

  const addItem = async () => {
    history.push(
      codebookRouteKnowledgeCheck(),
      {
        page: "edit"
      }
    );
  }

  const download = async (exportType: ExportTypeEnum) => {
    exportKnowledgeChecks(exportType);
  }

  return (
    <>
      <Row className="mt-5">
        <Col md={6}>
          <h3>{t("codeBook.knowledgeCheck")}</h3>
        </Col>
        {userCanEditData &&
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Button
              outline
              icon={<AddIcon />}
              onClick={addItem}
            >
              {t("codeBook.addItem")}
            </Button>
          </Col>
        }
      </Row>
      <Row className="mt-3">
        <Col md={6}>
          <TextField
            label={t("codeBook.knowledgeCheck")}
            name={nameof<typeof searchFilter>("name")}
            value={searchFilter.name}
            maxLength={80}
            onChange={handleChange(setSearchFilter)}
          />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center">
          <Button onClick={search}>
            {t("common.search")}
          </Button>
          <Typography color="primary" className={styles.clearFilters + " ml-5"} onClick={clearFilters}>
            {t("common.clearFilters")}
          </Typography>
        </Col>
      </Row>
      <Table  {...tableProps} />
      <Row className="mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Csv)}
          >
            {t("common.downloadDocumentExtension", { extension: ".csv" })}
          </Button>
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Excel)}
          >
            {t("common.downloadDocumentExtension", { extension: ".xslx" })}
          </Button>
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesDelete")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          {
            knowledgeCheckToDelete &&
            <Typography component="h1" variant="largeBold">
              {t("codeBook.knowledgeChecks.areYouSureDelete", { name: knowledgeCheckToDelete.name })}
            </Typography>
          }

        </div>
      </Modal>
    </>
  );
}